import { useState, useEffect, type RefObject, type ChangeEvent, type ElementType } from 'react';
import {
    Box,
    Button,
    Flex,
    HStack,
    VStack,
    Text,
    Icon,
    Input,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Divider,
} from '@chakra-ui/react';
import { MenuRow } from '../../ui';
import DualMonthRangeCalendar from '../../components/DualMonthRangeCalendar';
import { TbChevronDown, TbCalendar } from 'react-icons/tb';
import type { DatePreset } from './reportingTimeframeUtils';
import {
    PRESET_OPTIONS,
    clampReportingRangeDates,
    dateToIsoStr,
    getCustomGranularity,
    getGranularityForPreset,
    getLastTwoCalendarMonthsStart,
    getPresetDates,
    getReportingTimeframeTriggerLabel,
    getViewLeftMonthForAppliedRange,
    getLatestCompleteReportingDate,
    startOfLocalDay,
    type ReportingTimeframeApplyPayload,
} from './reportingTimeframeUtils';

export interface ReportingTimeframePopoverProps {
    committedPreset: DatePreset;
    committedStartDate: string;
    committedEndDate: string;
    onApply: (payload: ReportingTimeframeApplyPayload) => void;
    /** Row that contains this control (e.g. HStack with RefreshButton); clicks inside here do not count as “outside”. */
    boundaryRef: RefObject<HTMLElement | null>;
}

const ReportingTimeframePopover = ({
    committedPreset,
    committedStartDate,
    committedEndDate,
    onApply,
    boundaryRef,
}: ReportingTimeframePopoverProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [draftPreset, setDraftPreset] = useState<DatePreset>(committedPreset);
    const [showCustomPickers, setShowCustomPickers] = useState(false);
    const [popoverSession, setPopoverSession] = useState(0);
    const [customStart, setCustomStart] = useState<Date>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    });
    const [customEnd, setCustomEnd] = useState<Date>(() => getLatestCompleteReportingDate());

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isOpen) return;
            const target = event.target as HTMLElement;
            if (boundaryRef.current?.contains(target)) return;
            if (target.closest('.chakra-popover__content')) return;
            setIsOpen(false);
            setShowCustomPickers(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, boundaryRef]);

    const handleOpen = () => {
        setIsOpen(true);
        setPopoverSession((s) => s + 1);
        setDraftPreset(committedPreset);
        setShowCustomPickers(false);
        if (committedPreset === 'custom') {
            setCustomStart(startOfLocalDay(new Date(committedStartDate + 'T00:00:00')));
            setCustomEnd(startOfLocalDay(new Date(committedEndDate + 'T00:00:00')));
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setShowCustomPickers(false);
    };

    const handleDraftPresetSelect = (preset: DatePreset) => {
        setDraftPreset(preset);
        setShowCustomPickers(false);
    };

    const handleCustomRangeClick = () => {
        if (!showCustomPickers) {
            let s: Date;
            let e: Date;
            if (draftPreset === 'custom') {
                s = startOfLocalDay(customStart);
                e = startOfLocalDay(customEnd);
            } else {
                const d = getPresetDates(draftPreset);
                s = startOfLocalDay(new Date(d.startDate + 'T00:00:00'));
                e = startOfLocalDay(new Date(d.endDate + 'T00:00:00'));
            }
            const { start: clampedStart, end: clampedEnd } = clampReportingRangeDates(s, e);
            setCustomStart(clampedStart);
            setCustomEnd(clampedEnd);
        }
        setDraftPreset('custom');
        setShowCustomPickers(true);
    };

    const handleCalendarDraftChange = (start: Date, end: Date) => {
        if (!showCustomPickers) {
            setDraftPreset('custom');
            setShowCustomPickers(true);
        }
        const { start: s, end: e } = clampReportingRangeDates(start, end);
        setCustomStart(s);
        setCustomEnd(e);
    };

    const handleApplyClick = () => {
        if (showCustomPickers || draftPreset === 'custom') {
            const { start: s, end: e } = clampReportingRangeDates(customStart, customEnd);
            const startStr = dateToIsoStr(s);
            const endStr = dateToIsoStr(e);
            onApply({
                preset: 'custom',
                startDate: startStr,
                endDate: endStr,
                granularity: getCustomGranularity(startStr, endStr),
            });
        } else {
            const dates = getPresetDates(draftPreset);
            onApply({
                preset: draftPreset,
                startDate: dates.startDate,
                endDate: dates.endDate,
                granularity: getGranularityForPreset(draftPreset),
            });
        }
        setShowCustomPickers(false);
        setIsOpen(false);
    };

    const handleDismiss = () => {
        setShowCustomPickers(false);
        setIsOpen(false);
    };

    const parseMdyToDate = (raw: string): Date | null => {
        const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!m) return null;
        const d = new Date(+m[3], +m[1] - 1, +m[2]);
        if (Number.isNaN(d.getTime())) return null;
        if (d.getMonth() !== +m[1] - 1 || d.getDate() !== +m[2]) return null;
        return startOfLocalDay(d);
    };

    const handleFooterStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        const parsed = parseMdyToDate(e.target.value);
        if (!parsed) return;
        const endBase =
            draftPreset === 'custom' || showCustomPickers
                ? customEnd
                : startOfLocalDay(new Date(getPresetDates(draftPreset).endDate + 'T00:00:00'));
        const { start: s, end: ex } = clampReportingRangeDates(parsed, endBase);
        setDraftPreset('custom');
        setShowCustomPickers(true);
        setCustomStart(s);
        setCustomEnd(ex);
    };

    const handleFooterEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        const parsed = parseMdyToDate(e.target.value);
        if (!parsed) return;
        const startBase =
            draftPreset === 'custom' || showCustomPickers
                ? customStart
                : startOfLocalDay(new Date(getPresetDates(draftPreset).startDate + 'T00:00:00'));
        const { start: sx, end: ex } = clampReportingRangeDates(startBase, parsed);
        setDraftPreset('custom');
        setShowCustomPickers(true);
        setCustomStart(sx);
        setCustomEnd(ex);
    };

    const draftPresetDates = getPresetDates(draftPreset);
    const calendarRangeStart =
        showCustomPickers || draftPreset === 'custom'
            ? customStart
            : startOfLocalDay(new Date(draftPresetDates.startDate + 'T00:00:00'));
    const calendarRangeEnd =
        showCustomPickers || draftPreset === 'custom'
            ? customEnd
            : startOfLocalDay(new Date(draftPresetDates.endDate + 'T00:00:00'));

    const calendarInitialLeftMonth =
        showCustomPickers || draftPreset === 'custom'
            ? getLastTwoCalendarMonthsStart()
            : getViewLeftMonthForAppliedRange(draftPresetDates.endDate);

    const isoToMdy = (iso: string) => {
        const [y, m, d] = iso.split('-');
        return `${m}/${d}/${y}`;
    };
    const dateToMdy = (dt: Date) => {
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        return `${mm}/${dd}/${dt.getFullYear()}`;
    };
    const footerStartInputValue =
        draftPreset === 'custom' || showCustomPickers
            ? dateToMdy(customStart)
            : isoToMdy(draftPresetDates.startDate);
    const footerEndInputValue =
        draftPreset === 'custom' || showCustomPickers
            ? dateToMdy(customEnd)
            : isoToMdy(draftPresetDates.endDate);

    const triggerLabel = getReportingTimeframeTriggerLabel(
        committedPreset,
        committedStartDate,
        committedEndDate
    );

    return (
        <Popover
            isOpen={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            placement="bottom-end"
            closeOnBlur={false}
        >
            <PopoverTrigger>
                <Button
                    variant="outline"
                    bg="white"
                    borderColor="gray.300"
                    fontWeight="normal"
                    size="sm"
                    h="32px"
                    px={3}
                    minW="180px"
                    textAlign="left"
                    justifyContent="space-between"
                    leftIcon={<Icon as={TbCalendar as ElementType} boxSize={4} color="gray.500" />}
                    rightIcon={<Icon as={TbChevronDown as ElementType} boxSize={4} color="gray.500" />}
                    _hover={{ borderColor: 'gray.400' }}
                    _active={{ bg: 'white' }}
                    borderRadius="md"
                >
                    <Text fontSize="sm" color="gray.700" noOfLines={1}>
                        {triggerLabel}
                    </Text>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                w={{ base: '100%', md: '720px' }}
                maxW="96vw"
                p={0}
                overflow="hidden"
                boxShadow="none"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                zIndex={2000}
            >
                <PopoverBody p={0}>
                    <Flex align="stretch">
                        <Box
                            w="220px"
                            flexShrink={0}
                            borderRight="1px solid"
                            borderColor="gray.200"
                            py={2}
                        >
                            <VStack align="stretch" spacing={0}>
                                {PRESET_OPTIONS.map((option) => {
                                    const isSelected = option.value === draftPreset && !showCustomPickers;
                                    const label =
                                        typeof option.label === 'function' ? option.label() : option.label;
                                    return (
                                        <Box px={2} py={1} key={option.value}>
                                            <MenuRow
                                                kind="single"
                                                isSelected={isSelected}
                                                onClick={() => handleDraftPresetSelect(option.value)}
                                            >
                                                {label}
                                            </MenuRow>
                                        </Box>
                                    );
                                })}
                                <Divider />
                                <Box px={2} py={1}>
                                    <MenuRow
                                        kind="single"
                                        isSelected={showCustomPickers || draftPreset === 'custom'}
                                        onClick={handleCustomRangeClick}
                                    >
                                        Custom Range
                                    </MenuRow>
                                </Box>
                            </VStack>
                        </Box>
                        <Flex direction="column" flex={1} minW={0} maxW="100%">
                            <Box px={4} pt={4} pb={2} flex={1} minW={0} maxW="100%" overflow="hidden">
                                <DualMonthRangeCalendar
                                    key={popoverSession}
                                    startDate={calendarRangeStart}
                                    endDate={calendarRangeEnd}
                                    maxDate={getLatestCompleteReportingDate()}
                                    initialLeftMonth={calendarInitialLeftMonth}
                                    onChange={handleCalendarDraftChange}
                                />
                            </Box>
                            <Flex
                                borderTop="1px solid"
                                borderColor="gray.200"
                                px={4}
                                py={3}
                                align="center"
                                justify="space-between"
                                flexWrap="wrap"
                                gap={3}
                                flexShrink={0}
                            >
                                <HStack
                                    spacing={2}
                                    align="center"
                                    flex="1"
                                    minW={{ base: '100%', sm: '200px' }}
                                >
                                    <Input
                                        type="text"
                                        size="sm"
                                        placeholder="MM/DD/YYYY"
                                        value={footerStartInputValue}
                                        onChange={handleFooterStartDateChange}
                                        aria-label="Start date"
                                        flex="1"
                                        minW={0}
                                        borderColor="gray.300"
                                        fontSize="sm"
                                    />
                                    <Text fontSize="sm" color="gray.500" flexShrink={0}>
                                        –
                                    </Text>
                                    <Input
                                        type="text"
                                        size="sm"
                                        placeholder="MM/DD/YYYY"
                                        value={footerEndInputValue}
                                        onChange={handleFooterEndDateChange}
                                        aria-label="End date"
                                        flex="1"
                                        minW={0}
                                        borderColor="gray.300"
                                        fontSize="sm"
                                    />
                                </HStack>
                                <HStack spacing={2} flexShrink={0} ml={{ base: 'auto', sm: 0 }}>
                                    <Button
                                        size="sm"
                                        variant="outlineGray"
                                        fontWeight="semibold"
                                        color="gray.600"
                                        onClick={handleDismiss}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        bg="#0071E3"
                                        color="white"
                                        fontWeight="semibold"
                                        _hover={{ bg: '#3544a0' }}
                                        onClick={handleApplyClick}
                                    >
                                        Apply
                                    </Button>
                                </HStack>
                            </Flex>
                        </Flex>
                    </Flex>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default ReportingTimeframePopover;
